/* eslint-disable max-lines */

import React, { createContext, useContext, useState, useCallback } from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";

export type ToastVariant = "default" | "success" | "error" | "warning" | "info";

export interface ToastProps {
  id?: string;
  title: string;
  description: string;
  variant?: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
    altText: string;
  };
  onClose?: () => void;
  position?: "top" | "bottom";
  yOffset?: number;
}

// Toast context
interface ToastContextType {
  toasts: (ToastProps & { id: string })[];
  addToast: (toast: ToastProps) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const variantIcons = {
  default: Info,
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info
};

export interface ToastProviderProps extends ToastPrimitive.ToastProviderProps {
  children: React.ReactNode;
  position?: "top" | "bottom";
  yOffset?: number;
}

export function ToastProvider({
  children,
  position = "top",
  yOffset = 0,
  ...props
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<(ToastProps & { id: string })[]>([]);

  const addToast = useCallback(
    (toast: ToastProps): string => {
      const id = toast.id || crypto.randomUUID();
      setToasts(prev => [
        ...prev,
        {
          ...toast,
          id,
          position: toast.position || position,
          yOffset: toast.yOffset !== undefined ? toast.yOffset : yOffset
        }
      ]);
      return id;
    },
    [position, yOffset]
  );

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Group toasts by position
  const toastsByPosition = toasts.reduce(
    (acc, toast) => {
      const pos = toast.position || "top";
      if (!acc[pos]) acc[pos] = [];
      acc[pos].push(toast);
      return acc;
    },
    {} as Record<string, (ToastProps & { id: string })[]>
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      <ToastPrimitive.Provider {...props}>
        {children}
        {Object.entries(toastsByPosition).map(([pos, positionToasts]) => (
          <div key={pos}>
            {positionToasts.map(toast => (
              <ToastItem key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
            ))}
            <ToastViewport
              position={pos as "top" | "bottom"}
              yOffset={positionToasts[0]?.yOffset || 0}
            />
          </div>
        ))}
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

// Individual Toast Item
function ToastItem({
  title,
  description,
  variant = "default",
  duration = 5000,
  action,
  onClose
}: ToastProps & { id: string; onClose: () => void }) {
  const Icon = variantIcons[variant];

  return (
    <ToastPrimitive.Root
      data-testid="toast-root"
      className="group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border bg-slate-800 p-4 pr-8 text-white shadow-lg transition-all"
      duration={duration}
      onOpenChange={open => {
        if (!open && onClose) {
          onClose();
        }
      }}
    >
      <div className="flex items-center gap-4">
        {/* Text content */}
        <div className="flex-1">
          <ToastPrimitive.Title className="mb-1 flex items-center gap-2">
            <Icon
              className={cn("h-5 w-5", variant === "success" ? "text-green-400" : "text-blue-400")}
            />
            <span className="font-medium text-white">{title}</span>
          </ToastPrimitive.Title>
          <ToastPrimitive.Description className="text-sm text-gray-300">
            {description}
          </ToastPrimitive.Description>
        </div>
      </div>

      {action && (
        <ToastPrimitive.Action
          data-testid="toast-action-button"
          className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-gray-600 bg-transparent px-3 text-sm font-medium text-white hover:bg-slate-700"
          altText={action.altText}
          onClick={action.onClick}
        >
          {action.label}
        </ToastPrimitive.Action>
      )}

      <ToastPrimitive.Close
        data-testid="toast-close-button"
        className="absolute right-2 top-2 rounded-full p-1 text-white hover:bg-slate-700"
      >
        <X className="h-4 w-4" />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

export function ToastViewport({
  className,
  position = "top",
  yOffset = 0,
  style,
  ...props
}: React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport> & {
  position?: "top" | "bottom";
  yOffset?: number;
}) {
  const positionClasses =
    position === "bottom"
      ? "fixed bottom-0 left-0 right-0 z-[100] flex max-h-screen w-full flex-col p-4"
      : "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]";

  const offsetStyle = position === "bottom" && yOffset > 0 ? { bottom: `${yOffset}px` } : {};

  return (
    <ToastPrimitive.Viewport
      className={cn(positionClasses, className)}
      style={{ ...style, ...offsetStyle }}
      {...props}
    />
  );
}

// useToast hook
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  const { addToast, removeToast } = context;

  const toast = useCallback((props: ToastProps) => addToast(props), [addToast]);

  const dismiss = useCallback(
    (toastId?: string) => {
      if (toastId) {
        removeToast(toastId);
      }
    },
    [removeToast]
  );

  // Convenience methods for different toast types
  const success = useCallback(
    (props: Omit<ToastProps, "variant">) => toast({ ...props, variant: "success" }),
    [toast]
  );

  const error = useCallback(
    (props: Omit<ToastProps, "variant">) => toast({ ...props, variant: "error" }),
    [toast]
  );

  const warning = useCallback(
    (props: Omit<ToastProps, "variant">) => toast({ ...props, variant: "warning" }),
    [toast]
  );

  const info = useCallback(
    (props: Omit<ToastProps, "variant">) => toast({ ...props, variant: "info" }),
    [toast]
  );

  return {
    toast,
    success,
    error,
    warning,
    info,
    dismiss
  };
}

// Re-export primitive components for advanced usage
export { ToastPrimitive as Toast };
